import React, { useMemo } from 'react';
import {
    gql, useMutation, useQuery, useReactiveVar,
} from '@apollo/client';

import {
    ADD_ITEMS_TO_CART_BY_ID_LIST,
    ADD_ITEMS_TO_CART_BY_PERIOD,
    ADD_ITEMS_TO_CART_BY_SCHEMA,
} from 'app/graphql/network/basket/basketMutations';
import { selectedPeriodVar } from 'app/apollo/reaction';
import { setInitialPeriodAsQueryParam } from 'app/containers/contexts/filters.context/filters.context';
import {
    parseDishes, parseMenuType, parseTags,
} from 'app/utils/basketSchema';
import { useInitialBillingMutatation } from 'app/containers/BasketResolver';
import { usePreAssembledBasketType } from 'app/containers/contexts/filters.context/usePreAssembledBasketType';
import { useMenuPeriodsData } from './accessors';
import { BASKET_PAGE_ROOT_QUERY_INITIAL_COPY } from './accessors/useBasketData';

import { mealprepDishes } from '../../../landings/promo-checkout/src/pages/_disabled/mealprep/dishesData';

const useCartMutataionBySchema = () => {
    const { location } = window;

    const [
        addItemsToCartBySchema,
    ] = useMutation(ADD_ITEMS_TO_CART_BY_SCHEMA, {
        context: {
            message: 'app:mutate:BasketResolver',
        },
    });

    const type = usePreAssembledBasketType();

    const tags = useMemo(() => {
        if (type === 'green') {
            return ['1220'];
        }

        return parseTags(location) || [];
    }, [type, location]);

    return async (nextPeriod, period, update = () => { }, onCompleted = () => { }) => {
        await addItemsToCartBySchema({
            variables: {
                period: nextPeriod,
                menuType: parseMenuType(location),
                tags,
                schema: [{ category_id: 3, count: 10 }],
            },
            update,
            onCompleted,
        });
    };
};

const useCartMutationByPeriodOnly = () => {
    const [
        addItemsToCartByPeriod,
    ] = useMutation(ADD_ITEMS_TO_CART_BY_PERIOD, {
        context: {
            message: 'app:mutate:useCartMutationByPeriodOnly',
        },
    });

    return async (nextPeriod, period, update = () => { }, onCompleted = () => { }) => {
        await addItemsToCartByPeriod({
            variables: {
                period: nextPeriod,
                fillByPeriod: period,
            },
            update,
            onCompleted,
        });
    };
};

const useCartMutationsByIdList = () => {
    const { location } = window;

    const [
        addItemsToCartByIdList,
    ] = useMutation(ADD_ITEMS_TO_CART_BY_ID_LIST, {
        context: {
            message: 'app:mutate:useCartMutationsByIdList',
        },
    });

    return async (nextPeriod, period, update = () => { }, onCompleted = () => { }) => {
        await addItemsToCartByIdList({
            variables: {
                period: nextPeriod,
                dishes: parseDishes(location),
            },
            update,
            onCompleted,
        });
    };
};

const CART_DISHES = gql`
    query CART_SUBSCRIPTION(
    $period: String!
) {
    selectedPeriod @client @export(as: "period")

    cart(
        period: $period
    ) {
        period
        sections {
            id
            items {
                dish {
                    id
                }
            }
            cartType
        }
    }
}`;

const getDishIdsFromSection = (section) => section.items.map((i) => i?.dish?.id);

const getDishIds = (sections) => sections.reduce((acc, i) => [...getDishIdsFromSection(i), ...acc], []);

const useBasketMutationByContext = () => {
    const mutateBySchema = useCartMutataionBySchema();
    const mutateByPeriod = useCartMutationByPeriodOnly();
    const mutateByIdList = useCartMutationsByIdList();

    const { data } = useQuery(CART_DISHES, {
        fetchPolicy: 'cache-first',
    });


    const dishes = getDishIds(data?.cart?.sections || []);
    const hasCartMealprpeDishes = dishes.some((d) => mealprepDishes.includes(d));

    const params = new URLSearchParams(window.location.search);
    const setName = usePreAssembledBasketType();

    if (setName !== 'default') {
        return mutateBySchema;
    }

    if (params.has('dishes') && !hasCartMealprpeDishes) {
        return mutateByIdList;
    }

    return mutateByPeriod;
};

export const useBasketMutationByIdOrPeriod = () => {
    const { periods } = useMenuPeriodsData();
    const currentPeriod = useReactiveVar(selectedPeriodVar);

    /* DA / OPERATIONS  */
    const mutateBilling = useInitialBillingMutatation();
    const mutateCart = useBasketMutationByContext();

    /* HANDLERS */
    const mutateBasketByIdOrPeriod = async (day) => {
        /* PICK NEXT PERIOD */
        const selectedDate = new Date(day);

        console.log(day, 'day');
        console.log(selectedDate, 'selectedDate');

        const periodForDay = periods.find(({ start, end }) => {
            const startDate = new Date(start);
            const endDate = new Date(end);
            return selectedDate >= startDate && selectedDate <= endDate;
        });
        console.log(periodForDay, 'periodForDay');

        const { start: nextPeriod } = periodForDay;

        const update = (cache, { data }) => {
            const res = data?.addItemsToCartBySchema || data?.addItemsToCartByIdList || data?.addItemsToCartByPeriod;
            const { cart: { period } } = res;
            cache.writeQuery({
                query: BASKET_PAGE_ROOT_QUERY_INITIAL_COPY,
                variables: { period },
                data: { cart: res.cart },
            });
        };

        const onCompleted = (data) => {
            const res = data?.addItemsToCartBySchema || data?.addItemsToCartByIdList || data?.addItemsToCartByPeriod;
            const { period } = res.cart;

            setInitialPeriodAsQueryParam(period);
            selectedPeriodVar(period);
        };

        await mutateBilling();
        await mutateCart(nextPeriod, currentPeriod, update, onCompleted);
    };
    return mutateBasketByIdOrPeriod;
};
